import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Heart, MessageSquare, Search, ExternalLink, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getImageUrl = (photo) => {
  if (!photo) return null;
  const path = photo.storage_path;
  if (path && (path.startsWith('http://') || path.startsWith('https://'))) return path;
  return `${API}/files/${path}`;
};

const PropertyCard = ({ listing, onRemove, showRemove }) => {
  const navigate = useNavigate();
  const mainPhoto = listing.photos?.[0];
  return (
    <div className="border border-gray-300 bg-paper group" data-testid={`property-card-${listing.id}`}>
      <div className="aspect-[4/3] bg-gray-200 overflow-hidden cursor-pointer" onClick={() => navigate(`/listings/${listing.id}`)}>
        {mainPhoto ? (
          <img src={getImageUrl(mainPhoto)} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">No photo</div>
        )}
      </div>
      <div className="p-4">
        <div className="caps text-muted text-[10px] mb-1">{listing.neighbourhood}</div>
        <h3 className="font-serif text-lg leading-tight line-clamp-2 mb-2">{listing.title}</h3>
        <div className="text-xs text-muted mb-3">
          {listing.bedrooms} bed · {listing.bathrooms} bath
        </div>
        <div className="flex justify-between items-center border-t pt-3" style={{ borderTopColor: 'var(--line)' }}>
          <div className="font-serif text-lg">R{listing.monthly_price?.toLocaleString()}<span className="text-xs text-muted">/mo</span></div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/listings/${listing.id}`)}
              className="text-xs caps hover:underline"
              data-testid={`view-${listing.id}`}
            >
              <ExternalLink className="w-3 h-3 inline" strokeWidth={1.5} /> View
            </button>
            {showRemove && (
              <button
                onClick={(e) => { e.stopPropagation(); onRemove(listing.id); }}
                className="text-xs caps text-red-700 hover:underline"
                data-testid={`remove-fav-${listing.id}`}
              >
                <Trash2 className="w-3 h-3 inline" strokeWidth={1.5} /> Remove
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="border border-gray-300 p-6 bg-paper">
    <div className="caps text-muted mb-2">{label}</div>
    <div className="font-serif text-3xl md:text-4xl text-ink">{value}</div>
  </div>
);

export const GuestDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/guest/dashboard`, { withCredentials: true });
      setData(res);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavourite = async (listingId) => {
    try {
      await axios.delete(`${API}/favourites/${listingId}`, { withCredentials: true });
      toast.success('Removed from favourites');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-ink"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper text-ink">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="mb-10 pb-6 border-b" style={{ borderBottomColor: 'var(--line)' }}>
          <div className="flex items-baseline justify-between flex-wrap gap-4">
            <div>
              <div className="caps text-muted mb-2">Guest Dashboard</div>
              <h1 className="font-serif text-4xl md:text-5xl" data-testid="guest-dashboard-title">
                Welcome, <em className="italic">{user?.name?.split(' ')[0]}</em>
              </h1>
            </div>
            <Button
              onClick={() => navigate('/listings')}
              className="btn-editorial btn-solid"
              data-testid="browse-more-btn"
            >
              <Search className="w-4 h-4" strokeWidth={2} /> Browse Properties
            </Button>
          </div>
        </div>

        {/* Stats */}
        {data && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            <StatCard label="Saved Favourites" value={data.stats.favourites_count} />
            <StatCard label="Contacted" value={data.stats.contacted_count} />
            <StatCard label="Enquiries Sent" value={data.stats.enquiries_count} />
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="favourites" className="w-full">
          <TabsList className="mb-6 bg-transparent border-b border-gray-300 w-full justify-start rounded-none h-auto p-0">
            <TabsTrigger value="favourites" className="rounded-none caps data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:shadow-none" data-testid="guest-tab-favourites">
              Favourites ({data?.favourites?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="contacted" className="rounded-none caps data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:shadow-none" data-testid="guest-tab-contacted">
              Contacted ({data?.contacted?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="enquiries" className="rounded-none caps data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:shadow-none" data-testid="guest-tab-enquiries">
              My Enquiries ({data?.enquiries?.length || 0})
            </TabsTrigger>
          </TabsList>

          {/* Favourites */}
          <TabsContent value="favourites">
            {data?.favourites?.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-gray-400">
                <Heart className="w-10 h-10 mx-auto text-muted mb-4" strokeWidth={1.5} />
                <p className="font-serif text-xl italic text-muted mb-4">No favourites yet</p>
                <Button onClick={() => navigate('/listings')} className="btn-editorial btn-solid">
                  Browse properties →
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {data.favourites.map((listing) => (
                  <PropertyCard
                    key={listing.id}
                    listing={listing}
                    showRemove
                    onRemove={handleRemoveFavourite}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Contacted */}
          <TabsContent value="contacted">
            {data?.contacted?.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-gray-400">
                <MessageSquare className="w-10 h-10 mx-auto text-muted mb-4" strokeWidth={1.5} />
                <p className="font-serif text-xl italic text-muted">Properties you contact will appear here</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {data.contacted.map((listing) => (
                  <PropertyCard key={listing.id} listing={listing} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Enquiries */}
          <TabsContent value="enquiries">
            {data?.enquiries?.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-gray-400">
                <MessageSquare className="w-10 h-10 mx-auto text-muted mb-4" strokeWidth={1.5} />
                <p className="font-serif text-xl italic text-muted">Your enquiries will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {data.enquiries.map((e) => (
                  <div key={e.id} className="border border-gray-300 p-5 bg-paper" data-testid={`enquiry-${e.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="caps text-muted text-[10px] mb-1">Sent to host of</div>
                        <div className="font-serif text-lg">{e.listing_title}</div>
                      </div>
                      <div className="text-xs text-muted">{new Date(e.created_at).toLocaleDateString()}</div>
                    </div>
                    <p className="text-sm text-muted italic">&quot;{e.message}&quot;</p>
                    <button
                      onClick={() => navigate(`/listings/${e.listing_id}`)}
                      className="text-xs caps hover:underline mt-3"
                    >
                      View property →
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
